import { inject, Injectable } from '@angular/core';
import { IBaseDateChartDataValue } from '@app/api/interfaces/chart-data/base-date-chart-data';
import { IDateRange } from '@app/core/date-range/interfaces/date-range.interface';
import { DateRangeComparisonService } from '@app/core/date-range/services/date-range-comparison.service';
import { DayJSDateFormat } from '@app/core/enums/date-format.enum';
import { DayjsLocaleService } from '@app/core/i18n/services/dayjs-locale.service';
import { mapObjectBy } from '@app/shared/utils/array-mappers';

interface IManageCacheDataResult {
  isCacheInvalid: boolean;
  isCacheContainData: boolean;
  cacheDateRange: IDateRange;
  showDateRange: IDateRange;
  dateRangesForRequest: IDateRange[];
}

@Injectable({
  providedIn: 'root',
})
export class DateChartDataService {
  private dayjsLocale = inject(DayjsLocaleService);
  private dateRangeService = inject(DateRangeComparisonService);

  public manageCacheDate(
    cacheDateRange: IDateRange | null,
    date: string | null,
    monthsPeriod: number | null
  ): IManageCacheDataResult {
    const showDateRange = this.dateRangeService.getMonthsInRange(date, monthsPeriod, DayJSDateFormat.YYYYMMDD);

    const result: IManageCacheDataResult = {
      isCacheInvalid: true,
      isCacheContainData: false,
      cacheDateRange: showDateRange,
      showDateRange,
      dateRangesForRequest: [showDateRange],
    };

    if (!cacheDateRange) {
      return result;
    }

    const isCacheInvalid = !this.dateRangeService.isBaseRangeInvalid(cacheDateRange, showDateRange);

    if (!isCacheInvalid) {
      return result;
    }

    result.isCacheInvalid = false;

    const isExistingCacheContainData = this.dateRangeService.isRangeIntersecting(cacheDateRange, showDateRange);

    if (isExistingCacheContainData) {
      result.isCacheContainData = true;
      result.cacheDateRange = cacheDateRange;
      result.dateRangesForRequest = [];

      return result;
    }

    const dateRangesForRequest = this.dateRangeService.getRangesOutside(cacheDateRange, showDateRange);
    const newCacheDateRange = this.dateRangeService.getCombinedRange(cacheDateRange, showDateRange);

    result.cacheDateRange = newCacheDateRange;
    result.dateRangesForRequest = dateRangesForRequest;

    return result;
  }

  public fillAllEmptyDateDataAccordingToTheRange<T extends IBaseDateChartDataValue>(
    target: IDateRange,
    data: T[],
    generateEmptyObj: (date: string, prev: T | null, next: T | null) => T
  ): T[] {
    if (!data.length) {
      return data;
    }

    const targetStart = target.startDate ? this.dateRangeService.getTimestamp(target.startDate) : -Infinity;
    const targetEnd = target.endDate ? this.dateRangeService.getTimestamp(target.endDate) : Infinity;

    const existingDates = data.map(el => this.dateRangeService.getTimestamp(el.date));

    const existingStartDate = Math.min(...existingDates);
    const existingEndDate = Math.max(...existingDates);

    const startNormalized = targetStart === -Infinity ? existingStartDate : targetStart;
    const endNormalized = targetEnd === Infinity ? existingEndDate : targetEnd;

    const range = this.dateRangeService.formatResultRange(startNormalized, endNormalized);

    if (range.startDate && range.endDate) {
      const datesArray = this.generateDaysInDistinctRange(range.startDate, range.endDate);

      const dateMapByDate = mapObjectBy<T, string>(data, el => el.date);

      const prevByIndex: (T | null)[] = new Array(datesArray.length);
      let prev: T | null = null;
      for (let i = 0; i < datesArray.length; i++) {
        prev = dateMapByDate[datesArray[i]] ?? prev;
        prevByIndex[i] = prev;
      }

      const nextByIndex: (T | null)[] = new Array(datesArray.length);
      let next: T | null = null;
      for (let i = datesArray.length - 1; i >= 0; i--) {
        next = dateMapByDate[datesArray[i]] ?? next;
        nextByIndex[i] = next;
      }

      // this is also sort array by date!!!!
      return datesArray.map((date, i) => dateMapByDate[date] ?? generateEmptyObj(date, prevByIndex[i], nextByIndex[i]));
    }

    return this.sortByDate(data);
  }

  public filterDataAccordingToTheRange<T extends IBaseDateChartDataValue>(
    target: IDateRange,
    data: T[],
    isEmptyObject: (el: T) => boolean,
    showIfAllEmptyObject = false
  ): T[] {
    const targetStart = target.startDate ? this.dateRangeService.getTimestamp(target.startDate) : -Infinity;
    const targetEnd = target.endDate ? this.dateRangeService.getTimestamp(target.endDate) : Infinity;

    const notEmptyObjects = data.filter(el => !isEmptyObject(el));

    const dateThatCannotBeSkippet = notEmptyObjects.map(el => this.dateRangeService.getTimestamp(el.date));

    const dataStartDate = dateThatCannotBeSkippet.length ? Math.min(...dateThatCannotBeSkippet) : -Infinity;
    const dataEndDate = dateThatCannotBeSkippet.length ? Math.max(...dateThatCannotBeSkippet) : Infinity;

    const filterStart = targetStart === -Infinity ? dataStartDate : targetStart;
    const filterEnd = targetEnd === Infinity ? dataEndDate : targetEnd;

    const filteredData = data.filter(el => {
      const elDate = this.dateRangeService.getTimestamp(el.date);

      return filterStart <= elDate && elDate <= filterEnd;
    });

    const filteredNotEmptyObjects = filteredData.filter(el => !isEmptyObject(el));

    if (!showIfAllEmptyObject && !filteredNotEmptyObjects.length) {
      return [];
    }

    return this.sortByDate(filteredData);
  }

  private sortByDate<T extends IBaseDateChartDataValue>(data: T[]): T[] {
    return data.sort((a, b) => this.dateRangeService.getTimestamp(a.date) - this.dateRangeService.getTimestamp(b.date));
  }

  private generateDaysInDistinctRange(
    startDate: string,
    endDate: string,
    outputFormat: DayJSDateFormat = DayJSDateFormat.YYYYMMDD
  ): string[] {
    const startTs = this.dateRangeService.getTimestamp(startDate);
    const endTs = this.dateRangeService.getTimestamp(endDate);

    let current = this.dayjsLocale.dayjs(startTs);
    const end = this.dayjsLocale.dayjs(endTs);

    const days: string[] = [];

    while (current.isBefore(end) || current.isSame(end, 'day')) {
      days.push(current.format(outputFormat));
      current = current.add(1, 'day');
    }

    return days;
  }
}
